// Pás výhody s progress barem — zelený pás „kolik chybí do dopravy zdarma"
// z modalu „Možnosti doručení" na detailu (zadání Tomáše 2026-09-01). Košík
// a souhrn pokladny ho od 2026-09-02 nekreslí (handover „Objednávka v3": jeden
// společný bar se dvěma milníky v kartě košíku, staví checkoutPage.js);
// pokladna odsud bere jen `freeShippingState()` — stejná čísla jako modal.
// Markup drží tenhle builder, styly `components/_benefit-band.scss` (.bs-band).
//
// Zdroj dat pásu dopravy zdarma je dataLayer `cartInfo` (freeShippingState) —
// STEJNÁ čísla jako progress výhod v košíku. Nativní pás stránky možností
// dopravy se už nečte: fetch možností jede bez cookies (product-scoped,
// shippingModel.js) a o košíku nic neví.

import { icon } from './icon.js';
import { money } from './money.js';
import { shoptetData, cartLeft } from '../shoptet-hooks.js';
import { FREE_SHIP_SCOPE, FREE_SHIPPING } from './shippingModel.js';
import { NBSP } from './money.js';
import L from '../locale/cs/benefitBand.js';

// Věty pásu dopravy zdarma — sdílí je modal na detailu i košík/pokladna,
// aby zákazník četl všude totéž znění (vč. rozsahu „na výdejní místo").
export const FREE_LEFT_A = L.leftA;
export const FREE_LEFT_B = L.leftB(FREE_SHIP_SCOPE);
export const FREE_REACHED = L.reached(FREE_SHIP_SCOPE);
export const FREE_BAR_LABEL = L.barLabel;

function node(tag, className, content) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (content != null) el.textContent = content;
  return el;
}

const priceText = (value) => money(value);

function clampPercent(n) {
  return Math.max(0, Math.min(100, n));
}

/**
 * Postaví pás výhody: kulatá ikona, věta, volitelně progress bar.
 *
 * @param {object} opts
 * @param {string} opts.icon      jméno symbolu ze spritu ('truck', 'gift', …)
 * @param {Node[]|string} opts.msg obsah věty (uzly se přenesou, string se vloží)
 * @param {number|null} [opts.percent] postup 0–100; bez čísla se bar nekreslí
 * @param {string} [opts.barLabel] aria-label progress baru
 * @param {boolean} [opts.compact] kompaktní rozměry (souhrn pokladny)
 * @returns {HTMLElement}
 */
export function buildBand(opts) {
  const root = node('div', 'bs-band' + (opts.compact ? ' bs-band--compact' : ''));
  const badge = node('span', 'bs-band__icon');
  badge.appendChild(icon(opts.icon));
  root.appendChild(badge);

  const txt = node('div', 'bs-band__txt');
  const msg = node('span', 'bs-band__msg');
  if (typeof opts.msg === 'string') {
    msg.textContent = opts.msg;
  } else {
    opts.msg.forEach(function (part) {
      msg.appendChild(part);
    });
  }
  txt.appendChild(msg);

  if (typeof opts.percent === 'number' && Number.isFinite(opts.percent)) {
    const bar = node('span', 'bs-band__bar');
    bar.setAttribute('role', 'progressbar');
    if (opts.barLabel) bar.setAttribute('aria-label', opts.barLabel);
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    bar.setAttribute('aria-valuenow', String(Math.round(clampPercent(opts.percent))));
    const fill = node('span', 'bs-band__fill');
    fill.style.width = clampPercent(opts.percent) + '%';
    bar.appendChild(fill);
    txt.appendChild(bar);
  }
  root.appendChild(txt);
  return root;
}

/**
 * Stav dopravy zdarma z dataLayeru `cartInfo` (jediný zdroj čísel pásu).
 *
 * Chování platformy (ověřené v pokladně 2026-08-21, viz checkoutPage.js):
 *   • po dosažení je `leftToFreeShipping.priceLeft` ZÁPORNÝ, ne nula,
 *   • `freeShippingFrom` je `null` u košíku s produktem vyloučeným z dopravy
 *     zdarma (nadrozměr) → pás se NEkreslí, práh „od 2 000 Kč" by lhal.
 *
 * @returns {{shipFrom:number, shipLeft:number|null, leftText:string|null,
 *   reached:boolean, percent:number|null}|null} null = pás nemá co říct
 */
export function freeShippingState() {
  // shop bez dopravy zdarma (SK: žádná viditelná metoda ji neumí) ji
  // nekomunikuje ani tehdy, když práh přijde z dataLayeru
  if (!FREE_SHIPPING) return null;
  const info = shoptetData('cartInfo');
  if (!info) return null;
  const shipFrom = Number(info.freeShippingFrom) || null;
  if (!shipFrom) return null;

  const price = info.getNoBillingShippingPrice;
  const goods = price ? Number(price.withVat) : NaN;
  const leftRaw = cartLeft(info, 'leftToFreeShipping');
  const shipLeft = Number.isFinite(leftRaw) ? leftRaw : Number.isFinite(goods) ? shipFrom - goods : null;
  const reached = info.freeShipping === true || (shipLeft != null && shipLeft <= 0);
  const leftText =
    (info.leftToFreeShipping && info.leftToFreeShipping.formattedPrice) ||
    (shipLeft != null && shipLeft > 0 ? priceText(shipLeft) : null);
  const percent = reached
    ? 100
    : Number.isFinite(goods)
      ? clampPercent((goods / shipFrom) * 100)
      : shipLeft != null
        ? clampPercent(((shipFrom - shipLeft) / shipFrom) * 100)
        : null;
  return { shipFrom: shipFrom, shipLeft: shipLeft, leftText: leftText, reached: reached, percent: percent };
}

/**
 * Hotový pás „Objednejte ještě za X Kč a dopravu na výdejní místo máte
 * zdarma." / „Dopravu … máte zdarma." nad stavem z dataLayeru.
 *
 * @param {{compact?: boolean}} [opts]
 * @returns {HTMLElement|null} null, když pás nemá co říct (žádný práh)
 */
export function freeShippingBand(opts) {
  const s = freeShippingState();
  if (!s) return null;
  let msg;
  if (s.reached) {
    msg = FREE_REACHED;
  } else if (s.leftText) {
    msg = [
      document.createTextNode(FREE_LEFT_A),
      node('b', null, s.leftText.replace(/\s/g, NBSP)),
      document.createTextNode(FREE_LEFT_B),
    ];
  } else {
    return null;
  }
  return buildBand({
    icon: 'truck',
    msg: msg,
    percent: s.percent,
    barLabel: FREE_BAR_LABEL,
    compact: opts && opts.compact,
  });
}
